import { config } from './config';
import { logger } from './logger';

class MedusaClient {
  private baseUrl: string;
  private storeUrl: string;
  private publishableApiKey: string;

  constructor() {
    this.baseUrl = config.backend.url;
    this.storeUrl = config.backend.apiUrl;
    this.publishableApiKey = config.publishableApiKey;
  }

  private async request(endpoint: string, options: RequestInit = {}): Promise<Record<string, any>> {
    const url = `${this.storeUrl}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      'x-publishable-api-key': this.publishableApiKey,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include', // ✅ Include cookies for all requests
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        
        // If it's a publishable key error, provide a helpful message
        if (response.status === 400 && errorText.includes('Publishable API key required')) {
          throw new Error('Publishable API key required. Please set NEXT_PUBLIC_PUBLISHABLE_API_KEY in your environment variables.');
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  // Products
  async getProducts(params: {
    limit?: number;
    offset?: number;
    category_id?: string[];
    collection_id?: string[];
    q?: string;
    region_id?: string;
    expand?: string;
    tags?: string[];
    metadata?: Record<string, any>;
  } = {}) {
    const searchParams = new URLSearchParams();
    
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());
    if (params.category_id) params.category_id.forEach(id => searchParams.append('category_id[]', id));
    if (params.collection_id) params.collection_id.forEach(id => searchParams.append('collection_id[]', id));
    if (params.q) searchParams.append('q', params.q);
    if (params.region_id) searchParams.append('region_id', params.region_id);
    if (params.expand) searchParams.append('expand', params.expand);
    if (params.tags) params.tags.forEach(tag => searchParams.append('tags[]', tag));
    
    // Default to India region if not specified
    // if (!params.region_id) {
    //   searchParams.append('region_id', 'reg_01JZHKEXFMZ49T9PGHNDBMZKX8');
    // }

    return this.request(`/products?${searchParams.toString()}`);
  }

  async getProduct(id: string, params: {
    expand?: string;
    region_id?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.expand) searchParams.append('expand', params.expand);
    if (params.region_id) searchParams.append('region_id', params.region_id);

    return this.request(`/products/${id}?${searchParams.toString()}`);
  }

  // Faceted Search
  async facetedSearch(params: {
    product_type?: string;
    variants?: { [key: string]: string[] };
    metadata?: { [key: string]: string[] };
    price_range?: { min: number; max: number };
    tags?: string[];
    collections?: string[];
    category_handle?: string;
    limit?: number;
    offset?: number;
    sort?: 'price_asc' | 'price_desc' | 'newest' | 'featured';
  }) {
    return this.request('/products/faceted-search', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Categories
  async getCategories(params: {
    limit?: number;
    offset?: number;
    parent_category_id?: string;
    include_descendants_tree?: boolean;
  } = {}) {
    const searchParams = new URLSearchParams();
    
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());
    if (params.parent_category_id) searchParams.append('parent_category_id', params.parent_category_id);
    if (params.include_descendants_tree) searchParams.append('include_descendants_tree', 'true');

    return this.request(`/product-categories?${searchParams.toString()}`);
  }

  async getCategory(id: string, params: {
    expand?: string;
    include_descendants_tree?: boolean;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.expand) searchParams.append('expand', params.expand);
    if (params.include_descendants_tree) searchParams.append('include_descendants_tree', 'true');

    return this.request(`/product-categories/${id}?${searchParams.toString()}`);
  }

  // Regions
  async getRegions() {
    return this.request('/regions');
  }

  async getRegion(id: string) {
    return this.request(`/regions/${id}`);
  }

  // Cart
  async createCart(data: {
    region_id?: string;
    sales_channel_id?: string;
    country_code?: string;
    items?: Array<{
      variant_id: string;
      quantity: number;
    }>;
  } = {}) {
    return this.request('/carts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCart(id: string, params: {
    expand?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    if (params.expand) searchParams.append('expand', params.expand);

    return this.request(`/carts/${id}?${searchParams.toString()}`);
  }

  async updateCart(id: string, data: {
    region_id?: string;
    country_code?: string;
    email?: string;
    sales_channel_id?: string;
  }) {
    return this.request(`/carts/${id}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async addToCart(cartId: string, data: {
    variant_id: string;
    quantity: number;
    metadata?: Record<string, any>;
  }) {
    return this.request(`/carts/${cartId}/line-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCartItem(cartId: string, lineId: string, data: {
    quantity: number;
  }) {
    return this.request(`/carts/${cartId}/line-items/${lineId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeFromCart(cartId: string, lineId: string) {
    return this.request(`/carts/${cartId}/line-items/${lineId}`, {
      method: 'DELETE',
    });
  }

  // Customer
  async createCustomer(data: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async authenticateCustomer(data: {
    email: string;
    password: string;
  }) {
    return this.request('/auth/customer/emailpass', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCustomer(token: string) {
    return this.request('/customers/me', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // Store
  async getStore() {
    return this.request('/store');
  }

  // Collections
  async getCollections(params: {
    limit?: number;
    offset?: number;
  } = {}) {
    const searchParams = new URLSearchParams();
    
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());

    return this.request(`/collections?${searchParams.toString()}`);
  }

  async getCollection(id: string) {
    return this.request(`/collections/${id}`);
  }

  // Facet Master - Get available product types and facet configurations
  // TODO: This calls an admin endpoint from the client. Should be proxied through
  // an authenticated Next.js API route instead of calling the backend directly.
  async getFacetMaster() {
    try {
      const response = await fetch(`${this.baseUrl}/admin/facets`, {
        headers: {
          'Content-Type': 'application/json',
          'x-publishable-api-key': this.publishableApiKey,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch facet master: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching facet master:', error);
      return { success: false, data: { product_types: [] } };
    }
  }
}

export const medusaClient = new MedusaClient();
export default medusaClient;

// Utility for making authenticated requests to the backend API
export const backendApiCall = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const url = `${config.backend.url}${endpoint}`;
  
  const headers = {
    'Content-Type': 'application/json',
    'x-publishable-api-key': config.publishableApiKey,
    ...options.headers,
  };

  return fetch(url, {
    ...options,
    headers,
    credentials: 'include', // This ensures cookies are sent
  });
};

// Authentication specific API calls
export const authApi = {
  checkAuth: () => backendApiCall('/store/auth/me'),
  sendOtp: (phone: string) => backendApiCall('/store/auth/send-otp', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  }),
  // Firebase authentication methods
  authenticateWithFirebase: (idToken: string, name?: string) => backendApiCall('/store/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ idToken, name }),
  }),
  // Legacy OTP verification (for backward compatibility)
  verifyOtp: (phone: string, otp: string, name?: string) => backendApiCall('/store/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ phone, otp, name }),
  }),
  logout: () => backendApiCall('/store/auth/logout', {
    method: 'POST',
  }),
  updateProfile: (profileData: Record<string, any>) => backendApiCall('/store/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData),
  }),
}; 